import { useDirectusStore } from "@/modules/directus/store/directus";
import { createItem, readItem, readItems } from "@directus/sdk";
import { defineStore } from "pinia";
import ArticleInterface from "../interfaces/ArticleInterface";
import ArticleCategoryInterface from "../interfaces/ArticleCategoryInterface";
import { useLikeStore } from "@/modules/like/store/like";
import { ref, watch } from "vue";
import i18n from "@/plugins/i18n";

export const useJournalStore = defineStore("journal", () => {
  const directusStore = useDirectusStore();

  const likeStore = useLikeStore();

  const articles = ref<ArticleInterface[]>([]);
  const categories = ref<ArticleCategoryInterface[]>([]);
  const articlesBySub = ref<ArticleInterface[]>([]);

  const lastRequestedSub = ref<string | null>(null);

  const getArticles = async () => {
    articles.value = (await directusStore.city
      ?.request(
        readItems("articles", {
          fields: ["*", { article_categories: ["*"], user_created: ["*"] }],
          filter: {
            language: {
              _or: [
                { code: { _eq: i18n.global.locale.value } },
                { code: { _eq: null } },
              ],
            },
          },
          sort: ["-published_at"],
        })
      )
      .then((response) => {
        likeStore.getLikes(response);
        return response;
      })) as ArticleInterface[];
  };

  const getArticlesByCategory = (category: ArticleCategoryInterface) => {
    return directusStore.city
      ?.request(
        readItems("articles", {
          fields: ["*", { article_categories: ["*"], user_created: ["*"] }],
          filter: {
            article_categories: {
              article_categories_id: {
                id: category.id,
              },
            },
          },
          sort: ["-published_at"],
        })
      )
      .then((response) => {
        likeStore.getLikes(response);
        return response;
      }) as Promise<ArticleInterface[]>;
  };

  const getArticlesBySub = async (sub: string) => {
    lastRequestedSub.value = sub;
    articlesBySub.value = (await directusStore.city
      ?.request(
        readItems("articles", {
          fields: ["*", { article_categories: ["*"], user_created: ["*"] }],
          filter: {
            user_created: {
              external_identifier: {
                _eq: sub,
              },
            },
            language: {
              _or: [
                { code: { _eq: i18n.global.locale.value } },
                { code: { _eq: null } },
              ],
            },
          },
          sort: ["-published_at"],
        })
      )
      .then((response) => {
        likeStore.getLikes(response);
        return response;
      })) as ArticleInterface[];
  };

  const getArticle = (id: string) => {
    return directusStore.city
      ?.request(
        readItem("articles", id, {
          fields: ["*", { article_categories: ["*"], user_created: ["*"] }],
        })
      )
      .then((response) => {
        likeStore.getLikes([response]);
        return response;
      });
  };

  const getCategories = async () => {
    categories.value = (await directusStore.city?.request(
      readItems("article_categories", {
        fields: ["*", { child_categories: ["*"], parent_category: ["*"] }],
        filter: {
          language: { code: { _eq: i18n.global.locale.value } },
        },
      })
    )) as ArticleCategoryInterface[];
  };

  const createArticle = (article: Partial<ArticleInterface>) => {
    return directusStore.city?.request(createItem("articles", article));
  };

  watch(
    () => i18n.global.locale.value,
    () => {
      articles.value = [];
      articlesBySub.value = [];
      categories.value = [];
      getArticles();
      getCategories();
      if (lastRequestedSub.value) {
        getArticlesBySub(lastRequestedSub.value);
      }
    }
  );

  return {
    getArticles,
    getArticlesByCategory,
    getArticlesBySub,
    getArticle,
    getCategories,
    createArticle,
    articles,
    categories,
    articlesBySub,
  };
});
